import React, { useCallback, useEffect, useState } from "react";
import { Card, Table, Tag, Input, Select, Row, Col, Dropdown, Modal } from "antd";
import { DownOutlined, WarningOutlined } from "@ant-design/icons";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

const TrialUsers = () => {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [cancelledFilter, setCancelledFilter] = useState("");
  const [cancelingSubUserId, setCancelingSubUserId] = useState(null);

  const handleCancelSubscription = (user) => {
    Modal.confirm({
      className: "user-delete-confirm-modal",
      title: "Cancel Subscription?",
      content: (
        <div style={{ color: "#4b5563", marginTop: 8 }}>
          This will cancel the subscription at the end of the current billing period. The user will continue to have access until that date. The subscription will not renew.
        </div>
      ),
      okText: "Yes, Cancel at Period End",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: async () => {
        setCancelingSubUserId(user._id);
        try {
          await request({
            url: `${apiPath.authAdminUsersList}/${user._id}/subscription/cancel`,
            method: "POST",
            onSuccess: (data) => {
              ShowToast(data.message || "Subscription set to cancel at period end.", Severty.SUCCESS);
              // Update local state to reflect cancelAtPeriodEnd
              setUsers((prevUsers) =>
                prevUsers.map((prev) =>
                  prev._id === user._id ? { ...prev, subscriptionCancelAtPeriodEnd: true, cancelled: true } : prev
                )
              );
            },
            onError: (err) => {
              ShowToast(err?.response?.data?.message || err.message || "Failed to cancel subscription", Severty.ERROR);
            },
          });
        } finally {
          setCancelingSubUserId(null);
        }
      },
    });
  };

  const fetchPlans = useCallback(async () => {
    try {
      await request({
        url: apiPath.adminPlans,
        method: "GET",
        onSuccess: (response) => {
          setPlans(Array.isArray(response?.data) ? response.data : []);
        },
      });
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    }
  }, [request]);

  const fetchTrialUsers = useCallback(async (q = "", planId = "", isCancelled = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (planId) params.set("planId", planId);
      if (isCancelled !== undefined && isCancelled !== "") params.set("isCancelled", isCancelled);

      await request({
        url: `${apiPath.adminTrials}?${params.toString()}`,
        method: "GET",
        onSuccess: (response) => {
          setUsers(Array.isArray(response?.data) ? response.data : []);
        },
        onError: (error) => {
          ShowToast(
            error?.response?.data?.message || error?.message || "Failed to fetch trial users",
            Severty.ERROR,
          );
        },
      });
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    fetchTrialUsers(searchQuery, selectedPlan, cancelledFilter);
  }, [fetchTrialUsers, searchQuery, selectedPlan, cancelledFilter]);

  const columns = [
    { title: "User Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Plan",
      dataIndex: "trialPlanId",
      key: "plan",
      render: (plan) => plan?.name || "—",
    },
    {
      title: "Trial Started",
      dataIndex: "trialStartedAt",
      key: "trialStartedAt",
      render: (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : "—"),
    },
    {
      title: "Trial Ends",
      dataIndex: "trialEndsAt",
      key: "trialEndsAt",
      render: (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : "—"),
    },
    {
      title: "Converted",
      dataIndex: "converted",
      key: "converted",
      render: (value) => <Tag color={value ? "green" : "default"}>{value ? "Yes" : "No"}</Tag>,
    },
    {
      title: "Cancelled",
      dataIndex: "cancelled",
      key: "cancelled",
      render: (value) => <Tag color={value ? "red" : "default"}>{value ? "Yes" : "No"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "subscriptionStatus",
      key: "subscriptionStatus",
      render: (value, record) => {
        let color = "default";
        if (value === "active") color = "green";
        else if (value === "trialing") color = "blue";
        else if (value === "canceled") color = "red";
        else if (value === "past_due") color = "orange";
        
        const isClickable = (record.subscriptionStatus === "active" || record.subscriptionStatus === "trialing") && !record.subscriptionCancelAtPeriodEnd && !record.cancelled;
        
        const tag = (
          <Tag color={color} style={{ margin: 0, cursor: isClickable ? "pointer" : "default" }}>
            {value?.toUpperCase() || "NONE"}
          </Tag>
        );

        if (isClickable) {
          const items = [
            {
              key: '1',
              style: {
                backgroundColor: "rgba(230, 126, 61, 0.1)",
              },
              icon: <WarningOutlined style={{ fontSize: '15px', color: '#E67E3D', fontWeight: 'bold' }} />,
              label: <span style={{ fontWeight: 'bold', color: '#E67E3D' }}>Cancel Subscription</span>,
              onClick: (e) => {
                e.domEvent.stopPropagation();
                handleCancelSubscription(record);
              }
            }
          ];
          return (
            <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight" arrow>
              <div 
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer', paddingRight: '4px' }}
                onClick={e => e.stopPropagation()}
              >
                {tag}
                <DownOutlined style={{ fontSize: "12px", color: "#8c8c8c" }} />
              </div>
            </Dropdown>
          );
        }
        
        return tag;
      },
    },
  ];

  return (
    <Card bordered={false} className="criclebox" title="Trial Users">
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Input.Search
            placeholder="Search by name or email"
            allowClear
            onSearch={(value) => setSearchQuery(value)}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Select
            placeholder="Filter by Plan"
            allowClear
            style={{ width: "100%" }}
            onChange={(value) => setSelectedPlan(value || "")}
            options={[
              { label: "All Plans", value: "" },
              ...plans.map((p) => ({ label: p.name, value: p.id || p._id || "" })),
            ]}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Select
            placeholder="Filter by Cancelled"
            allowClear
            style={{ width: "100%" }}
            onChange={(value) => setCancelledFilter(value || "")}
            options={[
              { label: "All Status", value: "" },
              { label: "Cancelled", value: "true" },
              { label: "Active", value: "false" },
            ]}
          />
        </Col>
      </Row>

      <Table
        rowKey={(record) => record._id}
        loading={loading}
        columns={columns}
        dataSource={users}
        scroll={{ x: 1000 }}
      />
    </Card>
  );
};

export default TrialUsers;
